// API client for communicating with the backend server

const API_BASE_URL = import.meta.env.PROD 
  ? '/api' 
  : 'http://localhost:3001/api';

let sessionToken = null;

/**
 * Set session token for authenticated requests
 */
export const setSessionToken = (token) => {
  sessionToken = token;
};

/**
 * Get current session token
 */
export const getSessionToken = () => sessionToken;

/**
 * Clear session token
 */
export const clearSessionToken = () => {
  sessionToken = null;
};

/**
 * Make authenticated API request
 */
const apiRequest = async (endpoint, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (sessionToken) {
    headers['X-Session-Token'] = sessionToken;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });
  
  // For XML download endpoint, return response directly for special handling
  if (endpoint.includes('/xml')) {
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Request failed');
      } else {
        const text = await response.text();
        throw new Error(`Request failed with status ${response.status}`);
      }
    }
    return response;
  }
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  
  return response;
};

/**
 * Login user
 */
export const login = async (requestor, password) => {
  const response = await apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ requestor, password }),
  });
  
  const data = await response.json();
  setSessionToken(data.sessionToken);
  return data;
};

/**
 * Logout user
 */
export const logout = async () => {
  try {
    await apiRequest('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ sessionToken }),
    });
  } finally {
    clearSessionToken();
  }
};

/**
 * Get TIN for specific company
 */
export const getTIN = async (company) => {
  if (!company) {
    throw new Error('Company parameter is required');
  }
  
  const response = await apiRequest('/tin', {
    method: 'POST',
    body: JSON.stringify({ company }),
  });
  const data = await response.json();
  return data.tin;
};

/**
 * List invoices with filters
 */
export const listInvoices = async (filters = {}) => {
  if (!filters.company) {
    throw new Error('Company parameter is required');
  }
  
  const response = await apiRequest('/invoices/list', {
    method: 'POST',
    body: JSON.stringify(filters),
  });
  const data = await response.json();
  return data.data;
};

/**
 * Get invoice details
 */
export const getInvoice = async (invoiceId, company) => {
  if (!company) {
    throw new Error('Company parameter is required');
  }
  
  const response = await apiRequest('/invoices/detail', {
    method: 'POST',
    body: JSON.stringify({ company, invoiceId }),
  });
  const data = await response.json();
  return data.data;
};

/**
 * Get invoice products
 */
export const getInvoiceProducts = async (invoiceId, company) => {
  if (!company) {
    throw new Error('Company parameter is required');
  }
  
  const response = await apiRequest('/invoices/products', {
    method: 'POST',
    body: JSON.stringify({ company, invoiceId }),
  });
  const data = await response.json();
  return data.data;
};

/**
 * Download XML for invoice
 */
export const downloadXML = async (invoiceId, company) => {
  if (!company) {
    throw new Error('Company parameter is required');
  }
  
  const response = await apiRequest('/invoices/xml', {
    method: 'POST',
    body: JSON.stringify({ company, invoiceId }),
  });
  
  // Check if response is actually XML or an error JSON
  const contentType = response.headers.get('content-type');
  
  if (contentType && contentType.includes('application/json')) {
    const error = await response.json();
    throw new Error(error.details || error.error || 'Failed to generate XML');
  }
  
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `coretax_${company}_${invoiceId.replace(/[^a-zA-Z0-9]/g, '_')}.xml`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Download bulk XML for multiple invoices
 */
export const downloadBulkXML = async (invoiceIds, company) => {
  if (!company) {
    throw new Error('Company parameter is required');
  }
  
  if (!invoiceIds || invoiceIds.length === 0) {
    throw new Error('At least one invoice must be selected');
  }
  
  const response = await apiRequest('/invoices/xml-bulk', {
    method: 'POST',
    body: JSON.stringify({ company, invoiceIds }),
  });
  
  // Check if response is actually XML or an error JSON
  const contentType = response.headers.get('content-type');
  
  if (contentType && contentType.includes('application/json')) {
    const error = await response.json();
    throw new Error(error.details || error.error || 'Failed to generate bulk XML');
  }
  
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const timestamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
  a.download = `coretax_${company}_bulk_${timestamp}.xml`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Change user password
 */
export const changePassword = async (currentPassword, newPassword) => {
  const response = await apiRequest('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  const data = await response.json();
  return data;
};
